import React, { useEffect, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { fetchAddressbook, clear, createAddressbook, updateAddressbook } from '#action/addressbook/addressbook';
import { updateBreadcrumb } from '#action/breadcrumb';
import PreloaderComponent from '#component/preloader';
import Form from './form';
import { useError } from '#hooks';
import { setDocumentTitle } from '#helper';

/**
 * Renders addressbook form.
 * Prepares initial form values.
 * @returns {React.FC}
 */

const AddressbookForm = props => {
  const { match: { params: { id } }, history } = props;
  const dispatch = useDispatch();
  const { status, addressbook, error } = useSelector(s => s.addressbook, shallowEqual);
  const loading = id && status === 'loading';
  const pageDescription = id ? 'Edit address book' : 'Create address book';

  useError(error);

  useEffect(() => {
    if (id) {
      setDocumentTitle('Edit address book', addressbook.label);
    } else {
      setDocumentTitle('Create address book');
    }
    dispatch(updateBreadcrumb([
      { label: 'Address book', path: '/addressbook' },
      { label: pageDescription }
    ]));
  }, [addressbook.label, dispatch, id, pageDescription]);

  useEffect(() => {
    if (id) {
      dispatch(fetchAddressbook(id, true));
    }

    return () => {
      dispatch(clear());
    };
  }, [dispatch, id]);

  const initialValues = useMemo(() => ({
    label: addressbook.label ?? '',
    projectId: addressbook.project?.id ?? null
  }), [addressbook.label, addressbook.project?.id]);

  if (loading) {
    return <PreloaderComponent />;
  }

  const handleFormSubmit = async values => {
    if (id) {
      await dispatch(updateAddressbook(id, values));
    } else {
      await dispatch(createAddressbook(values));
    }
    history.push('/addressbook');
  };

  return (
    <Form
      {...props}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      pageDescription={pageDescription}
      addressbook={addressbook}
    />
  );
};

export default AddressbookForm;
